import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Footer from "@/components/Footer";
import { toast } from "sonner";

const LOGO = process.env.PUBLIC_URL + "/nextrota-logo.png";

/** Validates the new password matches our backend rules and returns a friendly
 * message describing what's missing - or null when it's strong enough. */
function validate(p) {
  if (p.length < 8) return "Use at least 8 characters.";
  if (!/[A-Z]/.test(p)) return "Include an uppercase letter.";
  if (!/[a-z]/.test(p)) return "Include a lowercase letter.";
  if (!/[0-9]/.test(p)) return "Include a number.";
  return null;
}

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const err = validate(pw);
    if (err) return toast.error(err);
    if (pw !== pw2) return toast.error("Passwords don't match.");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password: pw });
      setDone(true);
      toast.success("Password updated. Sign in with your new password.");
      setTimeout(() => navigate("/login"), 1800);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#F6F9FE]">
      <main className="flex-1 grid place-items-center px-6 py-12">
        <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 p-8 sm:p-10 shadow-sm">
          <Link to="/login" className="inline-block mb-6">
            <img src={LOGO} alt="NextRota" className="h-8 w-auto"/>
          </Link>
          <div className="eyebrow mb-2">Reset password</div>
          <h1 className="font-display text-3xl mb-3 text-[#0B1A36]">Choose a new password</h1>

          {!token ? (
            <p className="text-sm text-red-600">This link is missing a token. Please use the link in your email or <Link to="/forgot-password" className="underline">request a new one</Link>.</p>
          ) : !done ? (
            <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
              <div>
                <Label className="text-xs uppercase tracking-widest text-[#4B5773]">New password</Label>
                <Input required type="password" value={pw} data-testid="reset-pw"
                  onChange={(e)=>setPw(e.target.value)}
                  className="h-11 mt-2 rounded-lg border-slate-200"/>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-[#4B5773]">Confirm new password</Label>
                <Input required type="password" value={pw2} data-testid="reset-pw2"
                  onChange={(e)=>setPw2(e.target.value)}
                  className="h-11 mt-2 rounded-lg border-slate-200"/>
              </div>
              <p className="text-xs text-[#4B5773]">Use at least 8 characters, including a lowercase letter, an uppercase letter, a number and a special character such as ! @ # $ % ? or *.</p>
              <Button type="submit" disabled={busy} data-testid="reset-submit"
                className="w-full h-11 rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8]">
                {busy ? "Updating…" : "Update password"}
              </Button>
            </form>
          ) : (
            <p className="text-sm text-[#16A34A]" data-testid="reset-done">Password updated. Redirecting to sign in…</p>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
